import React, { createContext, useContext, useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase/config";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper function to sanitize email for Firebase key
  const sanitizeEmail = (email) => {
    return email.replace(/[.#$[\]]/g, "_");
  };

  useEffect(() => {
    // Listen for authentication state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in
        const sanitizedEmail = sanitizeEmail(firebaseUser.email);
        const userRef = doc(db, "ca_admin", sanitizedEmail);

        try {
          const snapshot = await getDoc(userRef);
          if (snapshot.exists()) {
            const userData = {
              id: firebaseUser.uid,
              email: firebaseUser.email,
              role: "super_admin",
              ...snapshot.data(),
            };
            setUser(userData);
            setIsAuthenticated(true);
          } else {
            // Create user data in Firestore if it doesn't exist
            const userData = {
              email: firebaseUser.email,
              role: "super_admin",
              createdAt: new Date().toISOString(),
            };
            await setDoc(userRef, userData);
            setUser({
              id: firebaseUser.uid,
              ...userData,
            });
            setIsAuthenticated(true);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setUser({
            id: firebaseUser.uid,
            email: firebaseUser.email,
            role: "super_admin",
          });
          setIsAuthenticated(true);
        }
      } else {
        // User is signed out
        setUser(null);
        setIsAuthenticated(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (credentials) => {
    try {
      // 1. First check if this email exists in our ca_admin collection
      const sanitizedEmail = sanitizeEmail(credentials.email);
      const userRef = doc(db, "ca_admin", sanitizedEmail);
      const snapshot = await getDoc(userRef);

      if (!snapshot.exists()) {
        return { success: false, error: "Invalid email" };
      }

      // 2. If email exists, attempt to sign in
      const userCredential = await signInWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password
      );

      return { success: true, user: userCredential.user };
    } catch (error) {
      let errorMessage = "Login failed";

      // If we are here, the email existed in Firestore but Auth failed
      // This almost always means the password was wrong (auth/wrong-password or auth/invalid-credential)
      switch (error.code) {
        case "auth/user-not-found":
        case "auth/invalid-email":
          errorMessage = "Invalid email";
          break;
        case "auth/wrong-password":
        case "auth/invalid-credential":
          errorMessage = "Invalid password";
          break;
        case "auth/too-many-requests":
          errorMessage = "Too many failed attempts. Please try again later";
          break;
        default:
          errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      console.error("Logout error:", error);
      return { success: false, error: error.message };
    }
  };

  // Function to create a new admin user (for initial setup)
  const createAdminUser = async (email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const sanitizedEmail = sanitizeEmail(email);
      const userRef = doc(db, "ca_admin", sanitizedEmail);

      const userData = {
        email: email,
        role: "super_admin",
        createdAt: new Date().toISOString(),
      };

      await setDoc(userRef, userData);

      return { success: true, user: userCredential.user };
    } catch (error) {
      let errorMessage = "Failed to create account";

      switch (error.code) {
        case "auth/email-already-in-use":
          errorMessage = "An account with this email already exists";
          break;
        case "auth/invalid-email":
          errorMessage = "Invalid email address";
          break;
        case "auth/weak-password":
          errorMessage = "Password should be at least 6 characters";
          break;
        default:
          errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  };

  // Function to delete a user account using Cloud Function
  const deleteUserAccount = async (uid) => {
    try {
      // Try to call the Cloud Function
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const deleteUserFunction = httpsCallable(functions, 'deleteUser');

      const result = await deleteUserFunction({ uid });

      if (result.data.success) {
        return { success: true, message: result.data.message };
      } else {
        return { success: false, error: result.data.error || 'Unknown error occurred' };
      }
    } catch (error) {
      console.error('Error calling deleteUser function:', error);

      // Return specific error messages based on error type
      if (error.code === 'functions/unauthenticated') {
        return { success: false, error: 'You must be authenticated to delete users.' };
      } else if (error.code === 'functions/permission-denied') {
        return { success: false, error: 'Only super admins can delete users.' };
      } else if (error.code === 'functions/not-found') {
        return { success: false, error: 'User not found in Firebase Authentication.' };
      } else if (error.code === 'functions/unavailable') {
        return {
          success: false,
          error: 'Cloud Function is not available. Please ensure it is deployed.',
          requiresManualDeletion: true
        };
      } else {
        return {
          success: false,
          error: `Failed to delete user: ${error.message}`,
          requiresManualDeletion: true
        };
      }
    }
  };

  // Function to update a user's password using Cloud Function
  const updateUserPassword = async (uid, newPassword) => {
    try {
      // Try to call the Cloud Function
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const updatePasswordFunction = httpsCallable(functions, 'updateUserPassword');

      const result = await updatePasswordFunction({ uid, newPassword });

      if (result.data.success) {
        return { success: true, message: result.data.message };
      } else {
        return { success: false, error: result.data.error || 'Unknown error occurred' };
      }
    } catch (error) {
      console.error('Error calling updateUserPassword function:', error);

      // Return specific error messages based on error type
      if (error.code === 'functions/unauthenticated') {
        return { success: false, error: 'You must be authenticated to update user passwords.' };
      } else if (error.code === 'functions/permission-denied') {
        return { success: false, error: 'Only super admins can update user passwords.' };
      } else if (error.code === 'functions/not-found') {
        return { success: false, error: 'User not found in Firebase Authentication.' };
      } else if (error.code === 'functions/invalid-argument') {
        return { success: false, error: 'Invalid password. Password must be at least 6 characters long.' };
      } else if (error.code === 'functions/unavailable') {
        return {
          success: false,
          error: 'Cloud Function is not available. Password updated in database only.',
          partialSuccess: true
        };
      } else {
        return {
          success: false,
          error: `Failed to update password in authentication: ${error.message}`,
          partialSuccess: true
        };
      }
    }
  };

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    createAdminUser,
    deleteUserAccount,
    updateUserPassword,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Note: Firebase Auth is now used for super admin authentication
// Users are stored in Firestore under ca_admin/[sanitized_email]
// The createAdminUser function can be used to create new admin accounts
